import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiArrowLeft, FiEdit, FiSettings, FiWifiOff, FiSearch } from 'react-icons/fi';
import { useChat } from '../../contexts/ChatContext';
import { useMemberAuth } from '../../contexts/MemberAuthContext';
import useChatPanelLayout, { MIN_WIDTH, MIN_HEIGHT } from '../../hooks/useChatPanelLayout';
import ConversationList from './ConversationList';
import MessageList from './MessageList';
import PinnedBar from './PinnedBar';
import MessageComposer from './MessageComposer';
import MemberPicker from './MemberPicker';
import MessageSearch from './MessageSearch';
import ThreadPanel from './ThreadPanel';
import ParticipantList from './ParticipantList';
import Avatar from './Avatar';

/**
 * Conversations window.
 *
 * A floating dialog that drops down from the header and lifts back up on close,
 * draggable by its title bar and resizable from any edge or corner. Position and
 * size are stored on the member's account (see useChatPanelLayout), so the
 * window reopens where they left it — including in a different browser.
 *
 * Below the `sm` breakpoint it is a full-screen sheet instead: dragging a window
 * around a 375px viewport is not a feature, and the saved desktop geometry would
 * put it off-screen. The saved layout is left untouched in that case rather than
 * being overwritten with phone-sized numbers.
 *
 * z-index note: header, modals, the music player and toasts all sit at z-50 in
 * this codebase, so the window takes z-[55] and its backdrop z-40 to land above
 * them. Toasts sit at z-[60] so they clear it — errors raised from inside the
 * window must stay visible.
 */

const DESKTOP_QUERY = '(min-width: 640px)';

/** Icon buttons on the white title bar: slate at rest, a sand wash on hover. */
const HEADER_BUTTON =
  'p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-neutral-200 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors';

/** Edges and corners, with the cursor and hit area each one gets. */
const HANDLES = [
  { dir: 'n', className: 'top-0 left-3 right-3 h-1.5 cursor-ns-resize' },
  { dir: 's', className: 'bottom-0 left-3 right-3 h-1.5 cursor-ns-resize' },
  { dir: 'w', className: 'left-0 top-3 bottom-3 w-1.5 cursor-ew-resize' },
  { dir: 'e', className: 'right-0 top-3 bottom-3 w-1.5 cursor-ew-resize' },
  { dir: 'nw', className: 'top-0 left-0 w-3 h-3 cursor-nwse-resize' },
  { dir: 'ne', className: 'top-0 right-0 w-3 h-3 cursor-nesw-resize' },
  { dir: 'sw', className: 'bottom-0 left-0 w-3 h-3 cursor-nesw-resize' },
  { dir: 'se', className: 'bottom-0 right-0 w-3 h-3 cursor-nwse-resize' },
];

/**
 * New geometry for a resize gesture.
 *
 * Dragging a west or north edge moves the origin as well as the size, and the
 * minimum has to be applied to the size *before* the origin is derived from it —
 * otherwise the window keeps sliding once it has stopped shrinking.
 */
function resizeFrom(origin, dir, dx, dy) {
  let { x, y, width, height } = origin;

  if (dir.includes('e')) width = origin.width + dx;
  if (dir.includes('s')) height = origin.height + dy;

  if (dir.includes('w')) {
    width = Math.max(MIN_WIDTH, origin.width - dx);
    x = origin.x + (origin.width - width);
  }
  if (dir.includes('n')) {
    height = Math.max(MIN_HEIGHT, origin.height - dy);
    y = origin.y + (origin.height - height);
  }

  return { x, y, width, height };
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    const query = window.matchMedia?.(DESKTOP_QUERY);
    if (!query) return undefined;
    const onChange = (event) => setIsDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

export default function ConversationsPanel() {
  const {
    isPanelOpen,
    closePanel,
    activeConversationId,
    closeConversation,
    conversations,
    connection,
    openConversation,
    thread,
  } = useChat();

  const navigate = useNavigate();
  const { member } = useMemberAuth();
  const isDesktop = useIsDesktop();
  const { layout, setLayout, resetLayout } = useChatPanelLayout(
    member?.id ?? null,
    isPanelOpen && isDesktop
  );

  const [view, setView] = useState('list'); // list | new | search
  const [showMembers, setShowMembers] = useState(false);
  const [gesturing, setGesturing] = useState(false);
  const gesture = useRef(null);

  const activeConversation = conversations.find((c) => c.id === activeConversationId);
  const showingThread = Boolean(activeConversationId);
  // Only a group has a "who is in here" question worth asking; for a DM the
  // title already answers it.
  const isGroup = (activeConversation?.participantCount || 0) > 2;

  // Switching conversations should land on the messages, not on the previous
  // conversation's member list.
  useEffect(() => {
    setShowMembers(false);
  }, [activeConversationId]);

  // Escape closes, as it does for every other dialog on the site.
  useEffect(() => {
    if (!isPanelOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPanelOpen, closePanel]);

  const startGesture = useCallback((event, next) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    gesture.current = { ...next, startX: event.clientX, startY: event.clientY };
    setGesturing(true);
  }, []);

  const beginDrag = (event) => {
    if (!isDesktop || event.button !== 0) return;
    // The title bar carries the back/search/new/settings/close buttons; a click
    // on one of those is not the start of a drag.
    if (event.target.closest('button')) return;
    startGesture(event, { type: 'drag', origin: layout });
  };

  const beginResize = (dir) => (event) => {
    if (!isDesktop || event.button !== 0) return;
    event.stopPropagation();
    startGesture(event, { type: 'resize', dir, origin: layout });
  };

  const onPointerMove = (event) => {
    const active = gesture.current;
    if (!active) return;

    const dx = event.clientX - active.startX;
    const dy = event.clientY - active.startY;

    if (active.type === 'drag') {
      setLayout({ ...active.origin, x: active.origin.x + dx, y: active.origin.y + dy });
    } else {
      setLayout(resizeFrom(active.origin, active.dir, dx, dy));
    }
  };

  const endGesture = (event) => {
    if (!gesture.current) return;
    gesture.current = null;
    setGesturing(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const gestureHandlers = {
    onPointerMove,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
  };

  const handleBack = () => {
    if (showMembers) {
      setShowMembers(false);
    } else if (showingThread) {
      closeConversation();
    } else {
      setView('list');
    }
  };

  const title = showMembers
    ? activeConversation?.name || 'Members'
    : showingThread
      ? activeConversation?.name || 'Conversation'
      : view === 'new'
        ? 'New conversation'
        : 'Conversations';

  // On a phone the window is the whole screen; on a desktop it is wherever the
  // member last put it.
  const frameStyle = isDesktop
    ? { left: layout.x, top: layout.y, width: layout.width, height: layout.height }
    : undefined;

  return (
    <AnimatePresence>
      {isPanelOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePanel}
            className="fixed inset-0 bg-primary/30 backdrop-blur-[2px] z-40"
            aria-hidden="true"
          />

          <motion.aside
            // Drops down on open, lifts back up on close.
            initial={{ opacity: 0, y: -18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -18, scale: 0.98 }}
            transition={{ type: 'tween', duration: 0.2 }}
            style={frameStyle}
            className={`fixed z-[55] bg-neutral-50 flex flex-col overflow-hidden
              shadow-[0_24px_60px_-22px_rgba(7,36,65,0.4)] ${
              isDesktop ? 'rounded-xl ring-1 ring-primary/10' : 'inset-0'
            } ${gesturing ? 'select-none' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label="Conversations"
          >
            <header
              onPointerDown={beginDrag}
              onDoubleClick={isDesktop ? resetLayout : undefined}
              {...(isDesktop ? gestureHandlers : {})}
              className={`relative flex items-center gap-2.5 px-4 py-3 bg-white
                border-b-[3px] border-accent ${
                isDesktop ? 'cursor-move touch-none' : ''
              }`}
              title={isDesktop ? 'Drag to move · double-click to reset' : undefined}
            >

              {(showingThread || view !== 'list' || showMembers) && (
                <button
                  onClick={handleBack}
                  className={HEADER_BUTTON}
                  aria-label="Back"
                >
                  <FiArrowLeft className="w-5 h-5" />
                </button>
              )}

              {showingThread && !showMembers && (
                <Avatar
                  name={activeConversation?.name}
                  seed={activeConversationId}
                  size="md"
                  variant="light"
                />
              )}

              <div className="flex-1 min-w-0">
                <h2 className="font-serif text-lg text-primary truncate leading-tight">{title}</h2>

                {/* The member count doubles as the way in to the full list —
                    a channel's participants are otherwise invisible. */}
                {showingThread && isGroup && !showMembers && (
                  <button
                    onClick={() => setShowMembers(true)}
                    className="text-xs text-primary/45 hover:text-primary hover:underline transition-colors"
                  >
                    {activeConversation.participantCount} members
                  </button>
                )}
              </div>

              {!showingThread && view === 'list' && (
                <>
                  <button
                    onClick={() => setView('search')}
                    className={HEADER_BUTTON}
                    aria-label="Search messages"
                  >
                    <FiSearch className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setView('new')}
                    className={HEADER_BUTTON}
                    aria-label="New conversation"
                  >
                    <FiEdit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      closePanel();
                      navigate('/member/preferences');
                    }}
                    className={HEADER_BUTTON}
                    aria-label="Conversation settings"
                  >
                    <FiSettings className="w-5 h-5" />
                  </button>
                </>
              )}

              <button
                onClick={closePanel}
                className={`${HEADER_BUTTON} -mr-1.5`}
                aria-label="Close conversations"
              >
                <FiX className="w-5 h-5" />
              </button>
            </header>

            {/* Surfaced rather than hidden: a member who does not know the socket
                dropped will think their messages sent. */}
            {(connection === 'disconnected' || connection === 'error') && (
              <div className="flex items-center gap-2 px-4 py-2 bg-accent/25 text-primary/80 text-xs border-b border-neutral-300">
                <FiWifiOff className="w-3.5 h-3.5 shrink-0" />
                Reconnecting…
              </div>
            )}
            {connection === 'expired' && (
              <div className="px-4 py-2 bg-alert/10 text-alert text-xs border-b border-neutral-300">
                Your session expired. Please sign in again.
              </div>
            )}

            <div className="flex-1 min-h-0 flex flex-col relative">
              {showMembers ? (
                <ParticipantList />
              ) : showingThread ? (
                <>
                  <PinnedBar />
                  <MessageList />
                  <MessageComposer />
                  {thread && <ThreadPanel />}
                </>
              ) : view === 'new' ? (
                <MemberPicker onDone={() => setView('list')} />
              ) : view === 'search' ? (
                <MessageSearch
                  onOpenResult={(conversationId) => {
                    setView('list');
                    openConversation(conversationId);
                  }}
                />
              ) : (
                <ConversationList />
              )}
            </div>

            {/* Resize handles sit above the content but are invisible; the only
                one that shows itself is the bottom-right grip, so the window
                does not look like a fixed box. */}
            {isDesktop && (
              <>
                {HANDLES.map(({ dir, className }) => (
                  <div
                    key={dir}
                    onPointerDown={beginResize(dir)}
                    {...gestureHandlers}
                    className={`absolute z-10 touch-none ${className}`}
                    aria-hidden="true"
                  />
                ))}
                <div
                  className="pointer-events-none absolute bottom-1 right-1 w-3 h-3 border-b-2 border-r-2 border-primary/20 rounded-br"
                  aria-hidden="true"
                />
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
